// A user without a group (an owner whose sign-up stopped half-way, say) has no permissions rather
// than taking the whole app down with them
const groupPermissions = user => user?.group?.permissions || []

const hasFrontendPermission = (permissionsNeeded, user) => {
  const permissions = groupPermissions(user)
  return permissions.includes('DO_ALL') || permissionsNeeded.every(r => permissions.includes(r))
}

const getUserPermissions = user => ({
  calendar: hasFrontendPermission(['VIEW_APPOINTMENTS'], user) || hasFrontendPermission(['VIEW_CREATED_APPOINTMENTS'], user),
  searchPatients:
    hasFrontendPermission(['VIEW_PATIENTS_DETAILS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_DETAILS'], user) ||
    hasFrontendPermission(['SEARCH_PATIENTS'], user) ||
    hasFrontendPermission(['SEARCH_CREATED_PATIENTS'], user),
  searchGroups: hasFrontendPermission(['VIEW_GROUPS'], user) || hasFrontendPermission(['VIEW_CREATED_GROUPS'], user),
  searchBranches: hasFrontendPermission(['VIEW_BRANCHES'], user) || hasFrontendPermission(['VIEW_CREATED_BRANCHES'], user),
  searchMessages: hasFrontendPermission(['VIEW_SMS'], user) || hasFrontendPermission(['VIEW_CREATED_SMS'], user),
  searchInventories: hasFrontendPermission(['VIEW_INVENTORY'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY'], user),
  branches: hasFrontendPermission(['VIEW_BRANCHES'], user) || hasFrontendPermission(['VIEW_CREATED_BRANCHES'], user),
  users: hasFrontendPermission(['VIEW_USERS'], user) || hasFrontendPermission(['VIEW_CREATED_USERS'], user),
  groups: hasFrontendPermission(['VIEW_GROUPS'], user) || hasFrontendPermission(['VIEW_CREATED_GROUPS'], user),
  medications: hasFrontendPermission(['VIEW_MEDICATIONS'], user) || hasFrontendPermission(['VIEW_CREATED_MEDICATIONS'], user),
  counters: hasFrontendPermission(['VIEW_COUNTER'], user) || hasFrontendPermission(['VIEW_CREATED_COUNTER'], user),
  getAllMedications:
    hasFrontendPermission(['VIEW_MEDICATIONS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_MEDICATIONS'], user) ||
    hasFrontendPermission(['ADD_PATIENTS_HISTORY'], user),
  messages: hasFrontendPermission(['VIEW_SMS'], user) || hasFrontendPermission(['VIEW_CREATED_SMS'], user),
  whatsapp: hasFrontendPermission(['VIEW_WA'], user) || hasFrontendPermission(['VIEW_CREATED_WA'], user),
  searchPatientReminders:
    hasFrontendPermission(['VIEW_PATIENTS_REMINDER'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_REMINDER'], user) ||
    hasFrontendPermission(['ADD_PATIENTS_REMINDER'], user),
  insuranceCompanies:
    hasFrontendPermission(['VIEW_INSURANCE_COMPANY'], user) || hasFrontendPermission(['VIEW_CREATED_INSURANCE_COMPANY'], user),
  claim: hasFrontendPermission(['VIEW_CLAIMS'], user) || hasFrontendPermission(['VIEW_CREATED_CLAIM'], user),
  labs: hasFrontendPermission(['VIEW_LAB'], user) || hasFrontendPermission(['VIEW_CREATED_LAB'], user),
  tasks: hasFrontendPermission(['VIEW_TASKS'], user) || hasFrontendPermission(['VIEW_CREATED_TASKS'], user),
  procedures: hasFrontendPermission(['VIEW_PROCEDURES'], user) || hasFrontendPermission(['VIEW_CREATED_PROCEDURES'], user),
  priceLists: hasFrontendPermission(['VIEW_PRICE_LIST'], user) || hasFrontendPermission(['VIEW_CREATED_PRICE_LIST'], user),
  inventory: hasFrontendPermission(['VIEW_INVENTORY'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY'], user),
  inventoryStorage:
    hasFrontendPermission(['VIEW_INVENTORY_STORAGE'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_STORAGE'], user),
  inventoryLocations:
    hasFrontendPermission(['VIEW_INVENTORY_LOCATION'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_LOCATION'], user),
  stockCounts: hasFrontendPermission(['VIEW_STOCK_COUNT'], user) || hasFrontendPermission(['VIEW_CREATED_STOCK_COUNT'], user),
  addStockCount: hasFrontendPermission(['ADD_STOCK_COUNT'], user),
  editStockCount: hasFrontendPermission(['EDIT_STOCK_COUNT'], user),
  deleteStockCount: hasFrontendPermission(['DELETE_STOCK_COUNT'], user),
  masterInventory:
    hasFrontendPermission(['VIEW_MASTER_INVENTORY'], user) || hasFrontendPermission(['VIEW_CREATED_MASTER_INVENTORY'], user),
  freeForms: hasFrontendPermission(['VIEW_FREE_FORMS'], user) || hasFrontendPermission(['VIEW_CREATED_FREE_FORMS'], user),
  createdAppointments: hasFrontendPermission(['VIEW_LOGS'], user) || hasFrontendPermission(['VIEW_CREATED_LOGS'], user),
  prescriptions:
    hasFrontendPermission(['VIEW_PATIENTS_HISTORY'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_HISTORY'], user),
  invoices:
    hasFrontendPermission(['VIEW_PATIENTS_INVOICE'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_INVOICE'], user),
  createdInvoices: hasFrontendPermission(['VIEW_CREATED_PATIENTS_INVOICE'], user),
  quotations:
    hasFrontendPermission(['VIEW_PATIENTS_QUOTATION'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_QUOTATION'], user),
  createdQuotations: hasFrontendPermission(['VIEW_CREATED_PATIENTS_QUOTATION'], user),
  payments:
    hasFrontendPermission(['VIEW_PATIENTS_PAYMENTS'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_PAYMENTS'], user),
  dashboardHomeInfo: hasFrontendPermission(['VIEW_DASHBOARD_CHARTS'], user),
  dashboard: hasFrontendPermission(['VIEW_DASHBOARD'], user),
  patientHomeInfo:
    hasFrontendPermission(['VIEW_PATIENTS_DETAILS', 'VIEW_DASHBOARD_CHARTS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_DETAILS', 'VIEW_DASHBOARD_CHARTS'], user),
  analytics: hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsReports: hasFrontendPermission(['VIEW_ANALYTICS_REPORTS'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsDoctors: hasFrontendPermission(['VIEW_ANALYTICS_DOCTORS'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsPatients: hasFrontendPermission(['VIEW_ANALYTICS_PATIENTS'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsAppointments:
    hasFrontendPermission(['VIEW_ANALYTICS_APPOINTMENTS'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsInvoices: hasFrontendPermission(['VIEW_ANALYTICS_INVOICES'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsQuotations:
    hasFrontendPermission(['VIEW_ANALYTICS_QUOTATIONS'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsPayments: hasFrontendPermission(['VIEW_ANALYTICS_PAYMENTS'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsOperations:
    hasFrontendPermission(['VIEW_ANALYTICS_OPERATIONS'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsTop25: hasFrontendPermission(['VIEW_ANALYTICS_TOP25'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsExpenses: hasFrontendPermission(['VIEW_ANALYTICS_EXPENSES'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsIncomes: hasFrontendPermission(['VIEW_ANALYTICS_INCOMES'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsInventory:
    hasFrontendPermission(['VIEW_ANALYTICS_INVENTORY'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsMasterInventory:
    hasFrontendPermission(['VIEW_ANALYTICS_MASTER_INVENTORY'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsIncomeStatement:
    hasFrontendPermission(['VIEW_ANALYTICS_INCOME_STATEMENT'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  analyticsLeads: hasFrontendPermission(['VIEW_ANALYTICS_LEADS'], user) || hasFrontendPermission(['VIEW_ANALYTICS'], user),
  adminPatients: hasFrontendPermission(['VIEW_ADMIN_PATIENTS'], user),
  adminAppointments: hasFrontendPermission(['VIEW_ADMIN_APPOINTMENTS'], user),
  adminTasks: hasFrontendPermission(['VIEW_ADMIN_TASKS'], user),
  adminCash: hasFrontendPermission(['VIEW_ADMIN_CASH'], user),
  adminSms: hasFrontendPermission(['VIEW_ADMIN_SMS'], user),
  adminInventory: hasFrontendPermission(['VIEW_ADMIN_INVENTORY'], user),
  adminMasterInventory: hasFrontendPermission(['VIEW_ADMIN_MASTER_INVENTORY'], user),
  adminOperations: hasFrontendPermission(['VIEW_ADMIN_OPERATIONS'], user),
  invoicesInfo:
    hasFrontendPermission(['VIEW_PATIENTS_INVOICE', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_INVOICE', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_PATIENTS_INVOICE', 'VIEW_ANALYTICS_INVOICES'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_INVOICE', 'VIEW_ANALYTICS_INVOICES'], user),
  quotationsInfo:
    hasFrontendPermission(['VIEW_PATIENTS_QUOTATION', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_QUOTATION', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_PATIENTS_QUOTATION', 'VIEW_ANALYTICS_QUOTATIONS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_QUOTATION', 'VIEW_ANALYTICS_QUOTATIONS'], user),
  paymentsInfo:
    hasFrontendPermission(['VIEW_PATIENTS_PAYMENTS', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_PAYMENTS', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_PATIENTS_PAYMENTS', 'VIEW_ANALYTICS_PAYMENTS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_PAYMENTS', 'VIEW_ANALYTICS_PAYMENTS'], user),
  labOrdersInfo:
    hasFrontendPermission(['VIEW_LAB', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_LAB', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_LAB', 'VIEW_ANALYTICS_LABS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_LAB', 'VIEW_ANALYTICS_LABS'], user),
  expensesInfo:
    hasFrontendPermission(['VIEW_EXPENSES', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_EXPENSES', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_EXPENSES', 'VIEW_ANALYTICS_EXPENSES'], user) ||
    hasFrontendPermission(['VIEW_CREATED_EXPENSES', 'VIEW_ANALYTICS_EXPENSES'], user),
  incomesInfo:
    hasFrontendPermission(['VIEW_INCOMES', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_INCOMES', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_INCOMES', 'VIEW_ANALYTICS_INCOMES'], user) ||
    hasFrontendPermission(['VIEW_CREATED_INCOMES', 'VIEW_ANALYTICS_INCOMES'], user),
  inventoryInfo:
    hasFrontendPermission(['VIEW_INVENTORY', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_INVENTORY', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_INVENTORY', 'VIEW_ANALYTICS_INVENTORY'], user) ||
    hasFrontendPermission(['VIEW_CREATED_INVENTORY', 'VIEW_ANALYTICS_INVENTORY'], user),
  masterInventoryInfo:
    hasFrontendPermission(['VIEW_MASTER_INVENTORY', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_MASTER_INVENTORY', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_MASTER_INVENTORY', 'VIEW_ANALYTICS_MASTER_INVENTORY'], user) ||
    hasFrontendPermission(['VIEW_CREATED_MASTER_INVENTORY', 'VIEW_ANALYTICS_MASTER_INVENTORY'], user),
  operationsInfo:
    hasFrontendPermission(['VIEW_PATIENTS_HISTORY', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_HISTORY', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_PATIENTS_HISTORY', 'VIEW_ANALYTICS_OPERATIONS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_HISTORY', 'VIEW_ANALYTICS_OPERATIONS'], user),
  treasuriesInfo:
    hasFrontendPermission(['VIEW_TREASURY', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_CREATED_TREASURY', 'VIEW_ANALYTICS'], user) ||
    hasFrontendPermission(['VIEW_TREASURY', 'VIEW_ANALYTICS_TREASURY'], user) ||
    hasFrontendPermission(['VIEW_CREATED_TREASURY', 'VIEW_ANALYTICS_TREASURY'], user),
  communicationsInfo:
    hasFrontendPermission(['VIEW_ANALYTICS'], user) || hasFrontendPermission(['VIEW_ANALYTICS_COMMUNICATIONS'], user),
  logs: hasFrontendPermission(['VIEW_LOGS'], user) || hasFrontendPermission(['VIEW_CREATED_LOGS'], user),
  download: hasFrontendPermission(['VIEW_DOWNLOAD'], user),
  inventoryTopUps: hasFrontendPermission(['VIEW_INVENTORY'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY'], user),
  inventoryUsage: hasFrontendPermission(['VIEW_INVENTORY'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY'], user),
  patients:
    hasFrontendPermission(['VIEW_PATIENTS_DETAILS'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_DETAILS'], user),
  patientMedical:
    hasFrontendPermission(['VIEW_PATIENTS_MEDICAL'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_MEDICAL'], user),
  medicals: hasFrontendPermission(['VIEW_MEDICAL'], user) || hasFrontendPermission(['VIEW_CREATED_MEDICAL'], user),
  noteTemplates: hasFrontendPermission(['VIEW_MEDICAL'], user) || hasFrontendPermission(['VIEW_CREATED_MEDICAL'], user),
  patientClinicalTests:
    hasFrontendPermission(['VIEW_PATIENTS_CLINICAL'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_CLINICAL'], user),
  patientClinicalTest:
    hasFrontendPermission(['VIEW_PATIENTS_CLINICAL'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_CLINICAL'], user),
  clinical: hasFrontendPermission(['VIEW_CLINICAL'], user) || hasFrontendPermission(['VIEW_CREATED_CLINICAL'], user),
  conditions: hasFrontendPermission(['VIEW_CONDITIONS'], user) || hasFrontendPermission(['VIEW_CREATED_CONDITIONS'], user),
  communications: hasFrontendPermission(['VIEW_LOGS'], user) || hasFrontendPermission(['VIEW_CREATED_LOGS'], user),
  patientReminders:
    hasFrontendPermission(['VIEW_PATIENTS_REMINDER'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_REMINDER'], user),
  getActiveReminders:
    hasFrontendPermission(['VIEW_PATIENTS_REMINDER'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_REMINDER'], user),
  monthlyExpenses: hasFrontendPermission(['VIEW_EXPENSES'], user) || hasFrontendPermission(['VIEW_CREATED_EXPENSES'], user),
  expenses: hasFrontendPermission(['VIEW_EXPENSES'], user) || hasFrontendPermission(['VIEW_CREATED_EXPENSES'], user),
  treasury: hasFrontendPermission(['VIEW_TREASURY'], user) || hasFrontendPermission(['VIEW_CREATED_TREASURY'], user),
  incomes: hasFrontendPermission(['VIEW_INCOMES'], user) || hasFrontendPermission(['VIEW_CREATED_INCOMES'], user),
  salaryAdjustments:
    hasFrontendPermission(['VIEW_SALARY_ADJUSTMENT'], user) || hasFrontendPermission(['VIEW_CREATED_SALARY_ADJUSTMENT'], user),
  files: hasFrontendPermission(['VIEW_FILES'], user) || hasFrontendPermission(['VIEW_CREATED_FILES'], user),
  patientFiles:
    hasFrontendPermission(['VIEW_PATIENTS_FILES'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_FILES'], user),
  patientXrays:
    hasFrontendPermission(['VIEW_PATIENTS_XRAYS'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_XRAYS'], user),
  getPatientOperations:
    hasFrontendPermission(['VIEW_PATIENTS_HISTORY'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_HISTORY'], user),
  patientPerioExam:
    hasFrontendPermission(['VIEW_PATIENTS_HISTORY'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_HISTORY'], user),
  getPatientNotes:
    hasFrontendPermission(['VIEW_PATIENTS_NOTES'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_NOTES'], user),
  // canals are measurements, part of the patient's history, though shown beside the notes
  getPatientCanals:
    hasFrontendPermission(['VIEW_PATIENTS_HISTORY'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_HISTORY'], user),
  userNotes:
    hasFrontendPermission(['VIEW_PATIENTS_NOTES'], user) ||
    hasFrontendPermission(['VIEW_CREATED_PATIENTS_NOTES'], user) ||
    hasFrontendPermission(['VIEW_PATIENTS_NOTES_LOGS'], user),
  createdOperations:
    hasFrontendPermission(['VIEW_PATIENTS_HISTORY'], user) || hasFrontendPermission(['VIEW_CREATED_PATIENTS_HISTORY'], user),
  doAll: hasFrontendPermission(['DO_ALL'], user),
  addNewGroup: hasFrontendPermission(['ADD_GROUPS'], user),
  editGroup: hasFrontendPermission(['EDIT_GROUPS'], user),
  deleteGroup: hasFrontendPermission(['DELETE_GROUPS'], user),
  addNewCounter: hasFrontendPermission(['ADD_COUNTER'], user),
  editCounter: hasFrontendPermission(['EDIT_COUNTER'], user),
  deleteCounter: hasFrontendPermission(['DELETE_COUNTER'], user),
  editCompanyFields: hasFrontendPermission(['EDIT_FIELDS'], user),
  deleteCompanyField: hasFrontendPermission(['EDIT_FIELDS'], user),
  addNewAppointment: hasFrontendPermission(['ADD_APPOINTMENTS'], user),
  editAppointment: hasFrontendPermission(['EDIT_APPOINTMENTS'], user),
  updateAppointmentStatus:
    hasFrontendPermission(['EDIT_APPOINTMENTS'], user) || hasFrontendPermission(['EDIT_APPOINTMENT_STATUS'], user),
  updateAppointmentTime: hasFrontendPermission(['EDIT_APPOINTMENTS'], user),
  deleteAppointment: hasFrontendPermission(['DELETE_APPOINTMENTS'], user),
  addNewBranch: hasFrontendPermission(['ADD_BRANCHES'], user),
  editBranch: hasFrontendPermission(['EDIT_BRANCHES'], user),
  disableBranch: hasFrontendPermission(['DELETE_BRANCHES'], user),
  disablePatient: hasFrontendPermission(['DELETE_PATIENTS_DETAILS'], user),
  addNewUser: hasFrontendPermission(['ADD_USERS'], user),
  editUser: hasFrontendPermission(['EDIT_USERS'], user),
  disableUser: hasFrontendPermission(['DELETE_USERS'], user),
  addNewMedication: hasFrontendPermission(['ADD_MEDICATIONS'], user),
  editMedication: hasFrontendPermission(['EDIT_MEDICATIONS'], user),
  deleteMedication: hasFrontendPermission(['DELETE_MEDICATIONS'], user),
  addNewPrescription: hasFrontendPermission(['ADD_PATIENTS_HISTORY'], user),
  editPrescription: hasFrontendPermission(['EDIT_PATIENTS_HISTORY'], user),
  deletePrescription: hasFrontendPermission(['DELETE_PATIENTS_HISTORY'], user),
  addNewInsuranceCompany: hasFrontendPermission(['ADD_INSURANCE_COMPANY'], user),
  // Three UI-facing names, one backend permission: each entity can be gated by its own flag
  // now, so per-entity backend permissions can be introduced later without revisiting call sites.
  addNewInsurancePolicy: hasFrontendPermission(['ADD_INSURANCE_COMPANY'], user),
  addNewPolicyClass: hasFrontendPermission(['ADD_INSURANCE_COMPANY'], user),
  editInsuranceCompany: hasFrontendPermission(['EDIT_INSURANCE_COMPANY'], user),
  deleteInsuranceCompany: hasFrontendPermission(['DELETE_INSURANCE_COMPANY'], user),
  addNewLab: hasFrontendPermission(['ADD_LAB'], user),
  editLab: hasFrontendPermission(['EDIT_LAB'], user),
  deleteLab: hasFrontendPermission(['DELETE_LAB'], user),
  addNewTask: hasFrontendPermission(['ADD_TASKS'], user),
  editTask: hasFrontendPermission(['EDIT_TASKS'], user),
  deleteTask: hasFrontendPermission(['DELETE_TASKS'], user),
  approveTask: hasFrontendPermission(['APPROVE_TASKS'], user),
  addNewMessage: hasFrontendPermission(['ADD_SMS'], user),
  editMessage: hasFrontendPermission(['EDIT_SMS'], user),
  deleteMessage: hasFrontendPermission(['DELETE_SMS'], user),
  sendMessage: hasFrontendPermission(['SEND_SMS'], user),
  addNewWhatsApp: hasFrontendPermission(['ADD_WA'], user),
  editWhatsApp: hasFrontendPermission(['EDIT_WA'], user),
  deleteWhatsApp: hasFrontendPermission(['DELETE_WA'], user),
  sendWhatsApp: hasFrontendPermission(['SEND_WA'], user),
  hideWhatsAppMessages: hasFrontendPermission(['HIDE_WHATSAPP_MESSAGES'], user),
  bulkAssignConversations: hasFrontendPermission(['BULK_ASSIGN_CONVERSATIONS'], user),
  updateProcedureOrder: hasFrontendPermission(['EDIT_PROCEDURES'], user),
  updateProcedureGroupOrder: hasFrontendPermission(['EDIT_PROCEDURES'], user),
  deleteProcedure: hasFrontendPermission(['DELETE_PROCEDURES'], user),
  addNewProcedureGroup: hasFrontendPermission(['ADD_PROCEDURES'], user),
  editProcedureGroup: hasFrontendPermission(['EDIT_PROCEDURES'], user),
  deleteProcedureGroup: hasFrontendPermission(['DELETE_PROCEDURES'], user),
  addNewPriceList: hasFrontendPermission(['ADD_PRICE_LIST'], user),
  editPriceList: hasFrontendPermission(['EDIT_PRICE_LIST'], user),
  updatePriceListPrices: hasFrontendPermission(['EDIT_PRICE_LIST'], user),
  deletePriceList: hasFrontendPermission(['DELETE_PRICE_LIST'], user),
  addNewInventory: hasFrontendPermission(['ADD_INVENTORY'], user),
  editInventory: hasFrontendPermission(['EDIT_INVENTORY'], user),
  topUpInventory: hasFrontendPermission(['ADD_INVENTORY'], user),
  deleteInventory: hasFrontendPermission(['DELETE_INVENTORY'], user),
  deleteProcedureUsage: hasFrontendPermission(['EDIT_PATIENTS_HISTORY'], user),
  deleteInventoryTopUp: hasFrontendPermission(['DELETE_INVENTORY'], user),
  addNewMasterInventory: hasFrontendPermission(['ADD_MASTER_INVENTORY'], user),
  editMasterInventory: hasFrontendPermission(['EDIT_MASTER_INVENTORY'], user),
  deleteMasterInventory: hasFrontendPermission(['DELETE_MASTER_INVENTORY'], user),
  addNewInventoryStorage: hasFrontendPermission(['ADD_INVENTORY_STORAGE'], user),
  editInventoryStorage: hasFrontendPermission(['EDIT_INVENTORY_STORAGE'], user),
  deleteInventoryStorage: hasFrontendPermission(['DELETE_INVENTORY_STORAGE'], user),
  editInventoryLocation: hasFrontendPermission(['EDIT_INVENTORY_LOCATION'], user),
  deleteInventoryLocation: hasFrontendPermission(['DELETE_INVENTORY_LOCATION'], user),
  addNewFreeForm: hasFrontendPermission(['ADD_FREE_FORMS'], user),
  editFreeForm: hasFrontendPermission(['EDIT_FREE_FORMS'], user),
  deleteFreeForm: hasFrontendPermission(['DELETE_FREE_FORMS'], user),
  addNewPatient: hasFrontendPermission(['ADD_PATIENTS_DETAILS'], user),
  editPatient: hasFrontendPermission(['EDIT_PATIENTS_DETAILS'], user),
  confirmPatientDetails: hasFrontendPermission(['ADD_PATIENTS_DETAILS'], user),
  updateMedicalHistory: hasFrontendPermission(['EDIT_PATIENTS_MEDICAL'], user),
  addMedicalHistory: hasFrontendPermission(['ADD_PATIENTS_MEDICAL'], user),
  updateClinicalTest: hasFrontendPermission(['EDIT_PATIENTS_CLINICAL'], user),
  deleteClinicalTest: hasFrontendPermission(['DELETE_PATIENTS_CLINICAL'], user),
  addNewMedical: hasFrontendPermission(['ADD_MEDICAL'], user),
  editMedical: hasFrontendPermission(['EDIT_MEDICAL'], user),
  deleteMedical: hasFrontendPermission(['DELETE_MEDICAL'], user),
  addNewClinicalItem: hasFrontendPermission(['ADD_CLINICAL'], user),
  editClinicalItem: hasFrontendPermission(['EDIT_CLINICAL'], user),
  deleteClinical: hasFrontendPermission(['DELETE_CLINICAL'], user),
  addNewConditionItem: hasFrontendPermission(['ADD_CONDITIONS'], user),
  editConditionItem: hasFrontendPermission(['EDIT_CONDITIONS'], user),
  deleteCondition: hasFrontendPermission(['DELETE_CONDITIONS'], user),
  addNewNoteTemplate: hasFrontendPermission(['ADD_MEDICAL'], user),
  editNoteTemplate: hasFrontendPermission(['EDIT_MEDICAL'], user),
  deleteNoteTemplate: hasFrontendPermission(['DELETE_MEDICAL'], user),
  addNewPatientReminder: hasFrontendPermission(['ADD_PATIENTS_REMINDER'], user),
  editPatientReminder: hasFrontendPermission(['EDIT_PATIENTS_REMINDER'], user),
  deletePatientReminder: hasFrontendPermission(['DELETE_PATIENTS_REMINDER'], user),
  addNewTreasury: hasFrontendPermission(['ADD_TREASURY'], user),
  editTreasury: hasFrontendPermission(['EDIT_TREASURY'], user),
  transferTreasury: hasFrontendPermission(['TRANSFER_TREASURY'], user),
  deleteTreasury: hasFrontendPermission(['DELETE_TREASURY'], user),
  addNewMonthlyExpense: hasFrontendPermission(['ADD_EXPENSES'], user),
  editMonthlyExpense: hasFrontendPermission(['EDIT_EXPENSES'], user),
  deleteMonthlyExpense: hasFrontendPermission(['DELETE_EXPENSES'], user),
  addNewExpense: hasFrontendPermission(['ADD_EXPENSES'], user),
  editExpense: hasFrontendPermission(['EDIT_EXPENSES'], user),
  deleteExpense: hasFrontendPermission(['DELETE_EXPENSES'], user),
  addNewIncome: hasFrontendPermission(['ADD_INCOMES'], user),
  editIncome: hasFrontendPermission(['EDIT_INCOMES'], user),
  deleteIncome: hasFrontendPermission(['DELETE_INCOMES'], user),
  addNewSalaryAdjustment: hasFrontendPermission(['ADD_SALARY_ADJUSTMENT'], user),
  editSalaryAdjustment: hasFrontendPermission(['EDIT_SALARY_ADJUSTMENT'], user),
  deleteSalaryAdjustment: hasFrontendPermission(['DELETE_SALARY_ADJUSTMENT'], user),
  addNewFile: hasFrontendPermission(['ADD_FILES'], user),
  editFile: hasFrontendPermission(['EDIT_FILES'], user),
  deleteFile: hasFrontendPermission(['DELETE_FILES'], user),
  addNewPatientFile: hasFrontendPermission(['ADD_PATIENTS_FILES'], user),
  editPatientFile: hasFrontendPermission(['EDIT_PATIENTS_FILES'], user),
  deletePatientFile: hasFrontendPermission(['DELETE_PATIENTS_FILES'], user),
  addNewPatientXray: hasFrontendPermission(['ADD_PATIENTS_XRAYS'], user),
  editPatientXray: hasFrontendPermission(['EDIT_PATIENTS_XRAYS'], user),
  deletePatientXray: hasFrontendPermission(['DELETE_PATIENTS_XRAYS'], user),
  addNewReferralSource: hasFrontendPermission(['EDIT_PATIENTS_DETAILS'], user),
  createNewOperations: hasFrontendPermission(['ADD_PATIENTS_HISTORY'], user),
  updateOperationsStatus: hasFrontendPermission(['EDIT_PATIENTS_HISTORY'], user),
  updateOperationsStatusOnly: hasFrontendPermission(['UPDATE_OPERATION_STATUS'], user),
  reverseOperationsStatus:
    hasFrontendPermission(['REVERSE_OPERATION_STATUS'], user) || hasFrontendPermission(['EDIT_PATIENTS_HISTORY'], user),
  saveOperations: hasFrontendPermission(['EDIT_PATIENTS_HISTORY'], user),
  deleteOperations: hasFrontendPermission(['DELETE_PATIENTS_HISTORY'], user),
  ungroupOperation: hasFrontendPermission(['EDIT_PATIENTS_HISTORY'], user),
  deleteAttendances: hasFrontendPermission(['DELETE_ATTENDANCE'], user),
  createNewInvoice: hasFrontendPermission(['ADD_PATIENTS_INVOICE'], user),
  editInvoice: hasFrontendPermission(['EDIT_PATIENTS_INVOICE'], user),
  deleteInvoice: hasFrontendPermission(['DELETE_PATIENTS_INVOICE'], user),
  createNewQuotation: hasFrontendPermission(['ADD_PATIENTS_QUOTATION'], user),
  editQuotation: hasFrontendPermission(['EDIT_PATIENTS_QUOTATION'], user),
  deleteQuotation: hasFrontendPermission(['DELETE_PATIENTS_QUOTATION'], user),
  addNewPayment: hasFrontendPermission(['ADD_PATIENTS_PAYMENTS'], user),
  deletePayment: hasFrontendPermission(['DELETE_PATIENTS_PAYMENTS'], user),
  editPayment: hasFrontendPermission(['EDIT_PATIENTS_PAYMENTS'], user),
  deleteInventoryUsage: hasFrontendPermission(['DELETE_INVENTORY'], user),
  deleteReminderInstance: hasFrontendPermission(['DELETE_PATIENTS_REMINDER'], user),
  updatePendingAmount: hasFrontendPermission(['EDIT_PATIENTS_INVOICE'], user),
  createNewNote: hasFrontendPermission(['ADD_PATIENTS_NOTES'], user),
  editNote: hasFrontendPermission(['EDIT_PATIENTS_NOTES'], user),
  deleteNotes: hasFrontendPermission(['DELETE_PATIENTS_NOTES'], user),
  createNewCanal: hasFrontendPermission(['ADD_PATIENTS_HISTORY'], user),
  editCanal: hasFrontendPermission(['EDIT_PATIENTS_HISTORY'], user),
  updateMixedDentition:
    hasFrontendPermission(['ADD_PATIENTS_HISTORY'], user) || hasFrontendPermission(['EDIT_PATIENTS_HISTORY'], user),
  updateHistoryAppointment: hasFrontendPermission(['EDIT_PATIENTS_HISTORY'], user),
  createNewReminderInstance: hasFrontendPermission(['ADD_PATIENTS_REMINDER'], user),
  editForm: hasFrontendPermission(['EDIT_BRANCHES'], user),
  savePerioExam: hasFrontendPermission(['EDIT_PATIENTS_HISTORY'], user),
  appointmentsCreatedAt: hasFrontendPermission(['APPOINTMENTS_CREATED_AT'], user),
  detailsCreatedAt: hasFrontendPermission(['PATIENTS_DETAILS_CREATED_AT'], user),
  expensesCreatedAt: hasFrontendPermission(['EXPENSES_CREATED_AT'], user),
  inventoryCreatedAt: hasFrontendPermission(['INVENTORY_CREATED_AT'], user),
  salaryAdjustmentCreatedAt: hasFrontendPermission(['SALARY_ADJUSTMENT_CREATED_AT'], user),
  paymentsCreatedAt: hasFrontendPermission(['PATIENTS_PAYMENTS_CREATED_AT'], user),
  invoiceCreatedAt: hasFrontendPermission(['PATIENTS_INVOICE_CREATED_AT'], user),
  editInvoiceTax: hasFrontendPermission(['EDIT_INVOICE_TAX'], user),
  quotationCreatedAt: hasFrontendPermission(['PATIENTS_QUOTATION_CREATED_AT'], user),
  historyCreatedAt: hasFrontendPermission(['PATIENTS_HISTORY_CREATED_AT'], user),
  notesCreatedAt: hasFrontendPermission(['PATIENTS_NOTES_CREATED_AT'], user),
  medicalCreatedAt: hasFrontendPermission(['PATIENTS_MEDICAL_CREATED_AT'], user),
  clinicalCreatedAt: hasFrontendPermission(['PATIENTS_CLINICAL_CREATED_AT'], user),
  reminderCreatedAt: hasFrontendPermission(['PATIENTS_REMINDER_CREATED_AT'], user),
  filesCreatedAt: hasFrontendPermission(['PATIENTS_FILES_CREATED_AT'], user),
  phoneNumbers: hasFrontendPermission(['VIEW_PATIENT_NUMBERS'], user),
  xraysCreatedAt: hasFrontendPermission(['PATIENTS_XRAYS_CREATED_AT'], user),
  editCompanyTypes: hasFrontendPermission(['EDIT_COMPANY_TYPES'], user),
  uploadLeads: hasFrontendPermission(['UPLOAD_LEADS'], user),
  uploadProcedures: hasFrontendPermission(['UPLOAD_PROCEDURES'], user),
  uploadStockCount: hasFrontendPermission(['UPLOAD_STOCK_COUNT'], user),
  // any upload the clinic does itself, which opens its upload page
  dataUpload:
    hasFrontendPermission(['UPLOAD_LEADS'], user) ||
    hasFrontendPermission(['UPLOAD_PROCEDURES'], user) ||
    hasFrontendPermission(['UPLOAD_STOCK_COUNT'], user),
  appointmentLogs: hasFrontendPermission(['VIEW_APPOINTMENTS_LOGS'], user),
  patientLogs: hasFrontendPermission(['VIEW_PATIENTS_DETAILS_LOGS'], user),
  invoiceLogs: hasFrontendPermission(['VIEW_PATIENTS_INVOICE_LOGS'], user),
  quotationLogs: hasFrontendPermission(['VIEW_PATIENTS_QUOTATION_LOGS'], user),
  paymentLogs: hasFrontendPermission(['VIEW_PATIENTS_PAYMENTS_LOGS'], user),
  historyLogs: hasFrontendPermission(['VIEW_PATIENTS_HISTORY_LOGS'], user),
  medicalLogs: hasFrontendPermission(['VIEW_PATIENTS_MEDICAL_LOGS'], user),
  deleteMedicalHistory: hasFrontendPermission(['DELETE_PATIENTS_MEDICAL'], user),
  editOperator: hasFrontendPermission(['EDIT_FINANCE_DOCTOR'], user),
  editOperatorForOperations: hasFrontendPermission(['EDIT_INVOICED_OPERATION_DOCTOR'], user),
  signDocuments: hasFrontendPermission(['SIGN_DOCUMENTS'], user),
  hideDashboardDates: groupPermissions(user).includes('HIDE_DASHBOARD_DATES'),
  timelineAppointment: hasFrontendPermission(['VIEW_TIMELINE_APPOINTMENT'], user),
  timelineNote: hasFrontendPermission(['VIEW_TIMELINE_NOTE'], user),
  timelineInvoice: hasFrontendPermission(['VIEW_TIMELINE_INVOICE'], user),
  timelineQuotation: hasFrontendPermission(['VIEW_TIMELINE_QUOTATION'], user),
  timelineOperation: hasFrontendPermission(['VIEW_TIMELINE_OPERATION'], user),
  timelinePrescription: hasFrontendPermission(['VIEW_TIMELINE_PRESCRIPTION'], user),
  timelinePatientForm: hasFrontendPermission(['VIEW_TIMELINE_PATIENT_FORM'], user),
  timelinePayment: hasFrontendPermission(['VIEW_TIMELINE_PAYMENT'], user),
  generateTimelineLink: hasFrontendPermission(['GENERATE_TIMELINE_LINK'], user),
  generatePatientLink: hasFrontendPermission(['GENERATE_LOGIN_LINK'], user),
  editPatientId: hasFrontendPermission(['EDIT_PATIENT_ID'], user),

  viewInventoryPurchases:
    hasFrontendPermission(['VIEW_INVENTORY_PURCHASE'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_PURCHASE'], user),
  addInventoryPurchases: hasFrontendPermission(['ADD_INVENTORY_PURCHASE'], user),
  editInventoryPurchases: hasFrontendPermission(['EDIT_INVENTORY_PURCHASE'], user),
  viewInventoryUsages:
    hasFrontendPermission(['VIEW_INVENTORY_USAGE'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_USAGE'], user),
  addInventoryUsages: hasFrontendPermission(['ADD_INVENTORY_USAGE'], user),
  editInventoryUsages: hasFrontendPermission(['EDIT_INVENTORY_USAGE'], user),
  viewInventoryTransfers:
    hasFrontendPermission(['VIEW_INVENTORY_TRANSFER'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_TRANSFER'], user),
  addInventoryTransfers: hasFrontendPermission(['ADD_INVENTORY_TRANSFER'], user),
  editInventoryTransfers: hasFrontendPermission(['EDIT_INVENTORY_TRANSFER'], user),
  viewInventoryDistributions:
    hasFrontendPermission(['VIEW_INVENTORY_DISTRIBUTION'], user) ||
    hasFrontendPermission(['VIEW_CREATED_INVENTORY_DISTRIBUTION'], user),
  addInventoryDistributions: hasFrontendPermission(['ADD_INVENTORY_DISTRIBUTION'], user),
  editInventoryDistributions: hasFrontendPermission(['EDIT_INVENTORY_DISTRIBUTION'], user),
  viewInventoryReturns:
    hasFrontendPermission(['VIEW_INVENTORY_RETURN'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_RETURN'], user),
  addInventoryReturns: hasFrontendPermission(['ADD_INVENTORY_RETURN'], user),
  editInventoryReturns: hasFrontendPermission(['EDIT_INVENTORY_RETURN'], user),
  viewInventorySales:
    hasFrontendPermission(['VIEW_INVENTORY_SALE'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_SALE'], user),
  addInventorySales: hasFrontendPermission(['ADD_INVENTORY_SALE'], user),
  editInventorySales: hasFrontendPermission(['EDIT_INVENTORY_SALE'], user),
  viewInventoryRefunds:
    hasFrontendPermission(['VIEW_INVENTORY_REFUND'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_REFUND'], user),
  addInventoryRefunds: hasFrontendPermission(['ADD_INVENTORY_REFUND'], user),
  editInventoryRefunds: hasFrontendPermission(['EDIT_INVENTORY_REFUND'], user),
  viewInventoryDisposals:
    hasFrontendPermission(['VIEW_INVENTORY_DISPOSAL'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_DISPOSAL'], user),
  addInventoryDisposals: hasFrontendPermission(['ADD_INVENTORY_DISPOSAL'], user),
  editInventoryDisposals: hasFrontendPermission(['EDIT_INVENTORY_DISPOSAL'], user),
  viewInventoryAdjustments:
    hasFrontendPermission(['VIEW_INVENTORY_ADJUSTMENT'], user) ||
    hasFrontendPermission(['VIEW_CREATED_INVENTORY_ADJUSTMENT'], user),
  addInventoryAdjustments: hasFrontendPermission(['ADD_INVENTORY_ADJUSTMENT'], user),
  editInventoryAdjustments: hasFrontendPermission(['EDIT_INVENTORY_ADJUSTMENT'], user),
  viewInventoryProfits:
    hasFrontendPermission(['VIEW_INVENTORY_PROFIT'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_PROFIT'], user),
  addInventoryProfits: hasFrontendPermission(['ADD_INVENTORY_PROFIT'], user),
  editInventoryProfits: hasFrontendPermission(['EDIT_INVENTORY_PROFIT'], user),
  viewInventoryBalance:
    hasFrontendPermission(['VIEW_INVENTORY_BALANCE'], user) || hasFrontendPermission(['VIEW_CREATED_INVENTORY_PROFIT'], user),
  addInventoryBalance: hasFrontendPermission(['ADD_INVENTORY_BALANCE'], user),
  editInventoryBalance: hasFrontendPermission(['EDIT_INVENTORY_BALANCE'], user),
  cannotEditInventoryAmounts:
    !user.group.permissions.includes('DO_ALL') && user.group.permissions.includes('DISABLE_EDIT_INVENTORY_AMOUNTS'),
  viewInventoryValues: hasFrontendPermission(['VIEW_INVENTORY_VALUES'], user),
  viewInventoryAmounts: hasFrontendPermission(['VIEW_INVENTORY_AMOUNTS'], user),
  viewInventoryLocationsValues: hasFrontendPermission(['VIEW_INVENTORY_LOCATIONS_VALUES'], user),
  viewInventoryLocationsAmounts: hasFrontendPermission(['VIEW_INVENTORY_LOCATIONS_AMOUNTS'], user),
  viewMasterValues: hasFrontendPermission(['VIEW_MASTER_INVENTORY_VALUES'], user),
  viewMasterAmounts: hasFrontendPermission(['VIEW_MASTER_INVENTORY_AMOUNTS'], user),
  viewMasterLocationsValues: hasFrontendPermission(['VIEW_MASTER_INVENTORY_LOCATIONS_VALUES'], user),
  viewMasterLocationsAmounts: hasFrontendPermission(['VIEW_MASTER_INVENTORY_LOCATIONS_AMOUNTS'], user),
  viewSystemChat: hasFrontendPermission(['VIEW_SYSTEM_CHAT'], user),
  editPatientPoints: hasFrontendPermission(['EDIT_PATIENTS_POINTS'], user),
  editLeadSettings: hasFrontendPermission(['EDIT_LEAD_SETTINGS'], user),
  moveMultipleLeads: hasFrontendPermission(['MOVE_MULTIPLE_LEADS'], user),
  viewLeads: hasFrontendPermission(['VIEW_LEAD'], user) || hasFrontendPermission(['VIEW_CREATED_LEAD'], user),
  viewAllLeads: hasFrontendPermission(['VIEW_LEAD'], user),
  addLeads: hasFrontendPermission(['ADD_LEAD'], user),
  editLeads: hasFrontendPermission(['EDIT_LEAD'], user),
  deleteLeads: hasFrontendPermission(['DELETE_LEAD'], user),
  viewFieldOperationsOnly: groupPermissions(user).includes('VIEW_FIELD_OPERATIONS_ONLY'),
  hideOperationPrices: groupPermissions(user).includes('HIDE_OPERATION_PRICES'),
  viewBusinessRegistration: hasFrontendPermission(['VIEW_BUSINESS_REGISTRATION'], user),
  editBusinessRegistration: hasFrontendPermission(['EDIT_BUSINESS_REGISTRATION'], user),
  viewEInvoiceSettings: hasFrontendPermission(['VIEW_EINVOICE_SETTINGS'], user),
  editEInvoiceSettings: hasFrontendPermission(['EDIT_EINVOICE_SETTINGS'], user),
  viewEInvoiceStatus: hasFrontendPermission(['VIEW_EINVOICE_STATUS'], user),
  // Patient E-Invoice permissions (old naming for backward compatibility)
  submitEInvoice: hasFrontendPermission(['REPORT_PATIENT_EINVOICE'], user),
  cancelEInvoice: hasFrontendPermission(['CANCEL_PATIENT_EINVOICE'], user),
  // Patient E-Invoice permissions (new naming)
  reportPatientEInvoice: hasFrontendPermission(['REPORT_PATIENT_EINVOICE'], user),
  cancelPatientEInvoice: hasFrontendPermission(['CANCEL_PATIENT_EINVOICE'], user),
  // Insurance E-Invoice permissions
  reportInsuranceEInvoice: hasFrontendPermission(['REPORT_INSURANCE_EINVOICE'], user),
  cancelInsuranceEInvoice: hasFrontendPermission(['CANCEL_INSURANCE_EINVOICE'], user),
  viewZatcaIntegration: hasFrontendPermission(['VIEW_ZATCA_INTEGRATION'], user),
  editZatcaIntegration: hasFrontendPermission(['EDIT_ZATCA_INTEGRATION'], user),
  // Claim permissions
  createClaim: hasFrontendPermission(['CREATE_CLAIM'], user),
  editClaim: hasFrontendPermission(['EDIT_CLAIM'], user),
  deleteClaim: hasFrontendPermission(['DELETE_CLAIM'], user),
  addInvoiceToClaim: hasFrontendPermission(['ADD_INVOICE_TO_CLAIM'], user),
  removeInvoiceFromClaim: hasFrontendPermission(['REMOVE_INVOICE_FROM_CLAIM'], user),
  transitionClaim: hasFrontendPermission(['TRANSITION_CLAIM'], user),
  viewClaimsAnalytics: hasFrontendPermission(['VIEW_CLAIMS_ANALYTICS'], user),

  // DHS permissions
  viewDhsIntegration: hasFrontendPermission(['VIEW_DHS_INTEGRATION'], user),
  editDhsIntegration: hasFrontendPermission(['EDIT_DHS_INTEGRATION'], user),
  viewDhsApprovals: hasFrontendPermission(['VIEW_DHS_APPROVALS'], user),
  checkDhsInsurance: hasFrontendPermission(['CHECK_DHS_INSURANCE'], user),
  checkDhsEligibility: hasFrontendPermission(['CHECK_DHS_ELIGIBILITY'], user),
  createDhsApproval: hasFrontendPermission(['CREATE_DHS_APPROVAL'], user),
  createManualDhsApproval: hasFrontendPermission(['CREATE_MANUAL_DHS_APPROVAL'], user),
  cancelDhsApproval: hasFrontendPermission(['CANCEL_DHS_APPROVAL'], user),
  getDhsApprovals: hasFrontendPermission(['GET_DHS_APPROVAL'], user)
})

export default getUserPermissions
